import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, ArrowDownLeft, ArrowUpRight, Trophy, Hash, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HolderHistory = () => {
  const { holderId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    load();
  }, [holderId]);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/tsb/explorer/holder/${holderId}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Holder not found');
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white pt-32 px-6">
        <div className="max-w-3xl mx-auto bg-red-500/10 border border-red-500/40 rounded-2xl p-6">
          <Link to="/explorer" className="text-blue-400 text-sm flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Back to Explorer</Link>
          <div className="text-red-300 font-bold">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-slate-950 text-white pt-32 px-6">Loading...</div>;

  const h = data.holder || {};
  const s = data.stats || {};

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-28 md:pt-32 pb-16 px-4 md:px-6" data-testid="holder-history-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/explorer" className="text-blue-400 text-sm flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Back to Explorer</Link>

        <div className="bg-gradient-to-br from-amber-500/20 via-slate-900/60 to-purple-500/10 border border-amber-500/30 rounded-2xl p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-slate-400 uppercase">TSB Holder</div>
              <h1 className="text-2xl md:text-3xl font-bold text-amber-300 mt-1 break-words">{h.label || '—'}</h1>
              {h.id && <div className="text-xs text-slate-500 font-mono mt-1">{h.id}</div>}
            </div>
            <span className="text-[10px] font-bold bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full flex items-center gap-1">
              {data.privacy_mode === 'off' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              Privacy: {data.privacy_mode === 'off' ? 'FULL' : 'MASKED'}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
            <Mini label="TSB Balance" value={`${fmt(data.tsb_balance)} TSB`} color="text-amber-300" />
            <Mini label="Received" value={`${fmt(s.received_volume_tsb)} TSB`} sub={`${s.received_count} tx`} color="text-emerald-300" />
            <Mini label="Sent" value={`${fmt(s.sent_volume_tsb)} TSB`} sub={`${s.sent_count} tx`} color="text-red-300" />
          </div>
        </div>

        <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Hash className="w-5 h-5 text-amber-400" /> Transaction History ({data.transactions.length})</h2>
        <div className="space-y-2" data-testid="holder-tx-list">
          {data.transactions.map((t) => {
            const isOut = t.direction === 'OUT';
            return (
              <div key={t.tx_id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-3">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">{t.type}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isOut ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                        {isOut ? 'SENT' : 'RECEIVED'}
                      </span>
                      <span className="text-[10px] text-slate-500">{new Date(t.timestamp).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 text-xs font-mono flex items-center gap-2">
                      {isOut ? <ArrowUpRight className="w-3 h-3 text-red-400" /> : <ArrowDownLeft className="w-3 h-3 text-emerald-400" />}
                      <span className="text-slate-400">{isOut ? 'TO' : 'FROM'}</span>
                      <span className={isOut ? 'text-amber-300' : 'text-emerald-300'}>{t.counterparty?.label || '—'}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono mt-1 truncate">Tx: {t.tx_id}</div>
                  </div>
                  <div className={`font-bold text-sm whitespace-nowrap ${t.currency === 'TSB' ? 'text-amber-300' : 'text-emerald-300'}`}>
                    {isOut ? '-' : '+'}{t.amount} {t.currency}
                  </div>
                </div>
              </div>
            );
          })}
          {data.transactions.length === 0 && <div className="text-slate-500 text-sm">No transactions yet</div>}
        </div>
      </div>
    </div>
  );
};

const Mini = ({ label, value, sub, color }) => (
  <div className="bg-slate-900/40 rounded-lg p-3 border border-slate-700/50">
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-lg font-bold mt-0.5 ${color}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
  </div>
);

export default HolderHistory;
